import {
  stubValidator,
  type LicenseStatus,
  type LicenseValidator,
  type RemoteLicenseValidator,
} from '../../core';

/**
 * Remote license validator (SP2, cue-forge-332.3). An async
 * {@link RemoteLicenseValidator} that checks a key against the license proxy
 * Worker (cue-forge-332.2) and maps its minimal contract onto the core
 * {@link LicenseStatus}. Offline grace and the synchronous cached read are
 * handled by `createLicense` (cue-forge-332.1); this only does the mapping.
 */

/** The proxy Worker's minimal response contract. */
interface ProxyResult {
  valid: boolean;
  status: 'active' | 'inactive' | 'expired' | 'disabled' | 'unknown';
  expiresAt: string | null;
  activationsLeft: number | null;
  error?: string;
}

/** Map the proxy contract onto a core LicenseStatus (grace logic lives in createLicense). */
export function statusFromProxy(p: ProxyResult): LicenseStatus {
  const state: LicenseStatus['state'] =
    p.status === 'expired'
      ? 'expired'
      : p.status === 'disabled'
        ? 'cancelled'
        : p.status === 'active' || p.status === 'inactive'
          ? 'active'
          : undefined;
  const status: LicenseStatus = { valid: p.valid, tier: 'pro' };
  if (state) status.state = state;
  if (p.expiresAt) {
    const ms = Date.parse(p.expiresAt);
    if (!Number.isNaN(ms)) status.expiresAt = ms;
  }
  return status;
}

/**
 * The synchronous validator for the **local** key path (cue-forge-sdv.1). On a
 * real production build local keys must never grant Pro — the bundled stub
 * checksum (and the {@link DEV_KEY}) is trivially mintable in DevTools, so it is
 * a tier affordance, never revenue protection. `allowLocal` therefore comes from
 * the build (`ALLOW_LOCAL_LICENSE`): dev, unit tests and the e2e build trust the
 * stub, but the live build collapses this to a reject-everything validator so
 * Pro can only come from the remote (Lemon Squeezy) validator.
 */
export function localValidator(allowLocal: boolean): LicenseValidator {
  if (allowLocal) return stubValidator;
  return { validate: (): LicenseStatus => ({ valid: false, tier: 'pro' }) };
}

/** Build a {@link RemoteLicenseValidator} that POSTs to the proxy's /validate. */
export function createRemoteValidator(endpoint: string): RemoteLicenseValidator {
  const base = endpoint.replace(/\/$/, '');
  return {
    async validate(key: string): Promise<LicenseStatus> {
      const resp = await fetch(`${base}/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ license_key: key }),
      });
      if (!resp.ok) throw new Error(`License check failed (${resp.status}).`);
      return statusFromProxy((await resp.json()) as ProxyResult);
    },
  };
}
